import React, { useState, useEffect, useMemo } from "react";
import AdminSidebar from "./AdminSidebar";

export default function UploadDomain() {
  const [domains, setDomains] = useState([]);
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    domain: "",
    price: "",
    category: "",
    description_en: "",
    description_it: "",
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [deletingId, setDeletingId] = useState(null);

  // Fetch domini
  const fetchDomains = async () => {
    try {
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;
      const res = await fetch(`${host}:${port}/api/v1/domains/get_domain`);
      const json = await res.json();
      if (res.ok && json.status === "OK") {
        const normalized = json.data.map((d) => ({
          ...d,
          domain_id: d.id || d.domain_id || d.id_domain,
          domain_name: d.domain_name || (d.domain ? d.domain.split(".")[0] : ""),
          domain_extension:
            d.domain_extension || (d.domain ? d.domain.split(".")[1] : ""),
          price: Number(d.price ?? d.domain_price) || 0,
          category_name: d.category_name || d.category || "",
          user_name: d.user_name || d.user || null,
        }));
        setDomains(normalized);
      } else {
        console.error("Errore fetch domini:", json);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch categorie
  const fetchCategories = async () => {
    try {
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;
      const res = await fetch(`${host}:${port}/api/v1/categories/get_category`);
      const json = await res.json();
      if (res.ok && json.status === "OK") {
        setCategories(json.data);
      } else {
        console.error("Errore fetch categorie:", json);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDomains();
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      const adminVariable = localStorage.getItem("admin");
      const token = localStorage.getItem("jwt_token");
      if (!adminVariable) throw new Error("Pagina non trovata");
      
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;

      const categoryId = parseInt(form.category, 10);
      if (!categoryId || isNaN(categoryId)) {
        setMessage("❌ Seleziona una categoria valida.");
        setLoading(false);
        return;
      }

      if (!form.domain.includes(".")) {
        setMessage(
          "❌ Il dominio deve essere nel formato nome.estensione (es: esempio.com)"
        );
        setLoading(false);
        return;
      }

      const priceFloat = parseFloat(form.price);
      if (isNaN(priceFloat) || priceFloat < 0) {
        setMessage("❌ Inserisci un prezzo valido.");
        setLoading(false);
        return;
      }

      const payload = {
        domain: form.domain.trim(),
        price: priceFloat,
        category: categoryId,
        description_it: form.description_it.trim(),
        description_en: form.description_en.trim(),
      };

      const res = await fetch(`${host}:${port}/api/v1/domains/add_domain`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (res.ok && json.status === "OK") {
        setMessage("✅ Dominio aggiunto!");
        setForm({
          domain: "",
          price: "",
          category: "",
          description_en: "",
          description_it: "",
        });
        fetchDomains();
      } else {
        setMessage(
          `❌ Errore: ${json.detail || json.message || JSON.stringify(json)}`
        );
      }
    } catch (err) {
      console.error(err);
      setMessage("Errore nella richiesta.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (domain_id, domain_full) => {
    if (!window.confirm(`Eliminare il dominio ${domain_full}?`)) return;
    setDeletingId(domain_id);
    try {
      const token = localStorage.getItem("jwt_token");
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;

      const res = await fetch(`${host}:${port}/api/v1/domains/delete_domain`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ domain_id }),
      });
      const json = await res.json();
      if (res.ok && json.status === "OK") {
        fetchDomains();
      } else {
        alert(
          "Errore durante la cancellazione: " +
            (json.detail || json.error || JSON.stringify(json))
        );
      }
    } catch (err) {
      alert("Errore nella richiesta.");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredDomains = useMemo(() => {
    return domains.filter((d) => {
      const domainFullName = `${d.domain_name}.${d.domain_extension}`.toLowerCase();
      const categoryName = (d.category_name || "").toLowerCase();
      if (filterName && !domainFullName.includes(filterName.toLowerCase()))
        return false;
      if (filterCategory && categoryName !== filterCategory.toLowerCase())
        return false;
      if (filterStatus === "available" && d.user_name) return false;
      if (filterStatus === "bought" && !d.user_name) return false;
      return true;
    });
  }, [domains, filterName, filterCategory, filterStatus]);

  const formatPrice = (p) => {
    if (typeof p !== "number" || isNaN(p)) return "-";
    return p.toFixed(2);
  };

  return (
    <div className="mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-black min-h-screen mr-12">
      <AdminSidebar className="bg-white shadow-md p-5 md:w-64 order-0 md:order-0" />

      <div className="md:col-span-2 grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-5 ml-4 xl:-ml-[15%] lg:ml-0">
        {/* Lista Domini */}
        <div
          className="bg-white shadow-lg p-4 w-full my-6 flex flex-col"
          style={{ maxHeight: "600px", overflowY: "auto" }}
        >
          <h3 className="text-lg font-semibold text-[#94c41c] mb-3">Lista Domini</h3>

          {/* Filtri */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Filtra per nome dominio"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 flex-grow min-w-[150px]"
            />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 min-w-[150px]"
            >
              <option value="">Tutte le categorie</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 min-w-[150px]"
            >
              <option value="all">Tutti</option>
              <option value="available">Disponibili</option>
              <option value="bought">Acquistati</option>
            </select>
          </div>

          <table className="table-auto w-full text-sm text-left text-black">
            <thead className="bg-[#94c41c] text-white sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2">Dominio</th>
                <th className="px-3 py-2">Prezzo (€)</th>
                <th className="px-3 py-2">Categoria</th>
                <th className="px-3 py-2">Stato</th>
                <th className="px-3 py-2">Utente</th>
                <th className="px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredDomains.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-600">
                    Nessun dominio trovato
                  </td>
                </tr>
              ) : (
                filteredDomains.map((d) => (
                  <tr key={d.domain_id} className="border-b border-gray-300">
                    <td className="px-3 py-1">
                      {d.domain_name}.{d.domain_extension}
                    </td>
                    <td className="px-3 py-1">{formatPrice(d.price)}</td>
                    <td className="px-3 py-1">{d.category_name}</td>
                    <td className="px-3 py-1">
                      {d.user_name ? "Acquistato" : "Disponibile"}
                    </td>
                    <td className="px-3 py-1">{d.user_name || "-"}</td>
                    <td className="px-3 py-1">
                      <button
                        className="text-red-600 hover:underline disabled:text-gray-400"
                        disabled={deletingId === d.domain_id}
                        onClick={() =>
                          handleDelete(
                            d.domain_id,
                            `${d.domain_name}.${d.domain_extension}`
                          )
                        }
                        style={{ outline: "none" }}
                        onMouseDown={(e) => e.preventDefault()} // evita il bordo focus click
                      >
                        {deletingId === d.domain_id ? "Eliminando..." : "Elimina"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Aggiungi Dominio */}
        <div
          className="bg-white shadow-lg p-4 w-full my-6 flex flex-col justify-center"
          style={{ height: "600px", overflow: "hidden" }}
        >
          <h3 className="text-lg font-semibold text-[#94c41c] mb-4">Aggiungi Dominio</h3>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3 text-black">
            <label className="text-black font-normal" htmlFor="domain">
              Dominio (nome.estensione)
            </label>
            <input
              id="domain"
              name="domain"
              type="text"
              value={form.domain}
              onChange={handleChange}
              placeholder="esempio.com"
              className="border border-black rounded px-3 py-1.5 text-black"
              required
            />

            <label className="text-black font-normal" htmlFor="price">
              Prezzo (€)
            </label>
            <input
              id="price"
              name="price"
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={handleChange}
              placeholder="es. 9.99"
              className="border border-black rounded px-3 py-1.5 text-black"
              required
            />

            <label className="text-black font-normal" htmlFor="category">
              Categoria
            </label>
            <select
              id="category"
              name="category"
              value={form.category}
              onChange={handleChange}
              className="border border-black rounded px-3 py-1.5 text-black"
              required
            >
              <option value="">Seleziona categoria</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            <label className="text-black font-normal" htmlFor="description_it">
              Descrizione IT
            </label>
            <textarea
              id="description_it"
              name="description_it"
              value={form.description_it}
              onChange={handleChange}
              placeholder="Descrizione in italiano"
              className="border border-black rounded px-3 py-1.5 text-black resize-none"
              rows={2}
            />

            <label className="text-black font-normal" htmlFor="description_en">
              Descrizione EN
            </label>
            <textarea
              id="description_en"
              name="description_en"
              value={form.description_en}
              onChange={handleChange}
              placeholder="Description in English"
              className="border border-black rounded px-3 py-1.5 text-black resize-none"
              rows={2}
            />

            <button
              type="submit"
              disabled={loading}
              className="bg-[#94c41c] text-white font-semibold py-2 rounded hover:bg-green-700 disabled:bg-green-300"
              style={{ outline: "none" }}
            >
              {loading ? "Caricamento..." : "Aggiungi Dominio"}
            </button>
          </form>

          {message && (
            <p
              className={`mt-3 ${
                message.startsWith("✅") ? "text-green-700" : "text-red-700"
              } font-semibold`}
            >
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
